import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Star, Loader2, Save, Trash2, GripVertical, Calendar, Clock,
  Search, Plus, Image, MapPin, AlertCircle, Megaphone
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { es } from 'date-fns/locale';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import api from '../../lib/api';

const MAX_FEATURED_EVENTS = 3;

const SortableFeaturedEvent = ({ item, event, onRemove, onUpdateSchedule }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.event_id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  if (!event) {
    return (
      <div ref={setNodeRef} style={style} className="bg-zinc-900 border border-red-500/30 rounded-xl p-4 flex items-center gap-4">
        <button {...attributes} {...listeners} className="text-zinc-500 cursor-grab"><GripVertical size={20} /></button>
        <div className="flex-1"><p className="text-red-400 text-sm">Evento no encontrado (ID: {item.event_id})</p></div>
        <Button variant="ghost" size="sm" onClick={() => onRemove(item.event_id)} className="text-red-400"><Trash2 size={16} /></Button>
      </div>
    );
  }

  const eventDate = new Date(event.date);

  return (
    <div ref={setNodeRef} style={style} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors">
      <div className="flex items-start gap-4">
        <button {...attributes} {...listeners} className="mt-2 text-zinc-500 hover:text-zinc-300 cursor-grab active:cursor-grabbing">
          <GripVertical size={20} />
        </button>
        <div className="w-24 h-16 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
          {event.image_url ? (
            <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><Image size={20} className="text-zinc-600" /></div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-white truncate">{event.title}</h3>
          <div className="flex items-center gap-4 mt-1 text-sm text-zinc-400">
            <span className="flex items-center gap-1"><Calendar size={12} className="text-[#D4AF37]" />{formatInTimeZone(eventDate, 'America/Argentina/Buenos_Aires', "d MMM yyyy", { locale: es })}</span>
            <span className="flex items-center gap-1"><Clock size={12} className="text-[#D4AF37]" />{formatInTimeZone(eventDate, 'America/Argentina/Buenos_Aires', "HH:mm")}hs</span>
            <span className="flex items-center gap-1 truncate"><MapPin size={12} className="text-[#D4AF37]" />{event.location}</span>
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-3">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-zinc-500">Desde:</Label>
              <Input type="date" value={item.start_date?.split('T')[0] || ''} onChange={(e) => onUpdateSchedule(item.event_id, 'start_date', e.target.value ? new Date(e.target.value).toISOString() : null)} className="h-8 w-36 bg-zinc-800 border-zinc-700 text-sm" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-zinc-500">Hasta:</Label>
              <Input type="date" value={item.end_date?.split('T')[0] || ''} onChange={(e) => onUpdateSchedule(item.event_id, 'end_date', e.target.value ? new Date(e.target.value).toISOString() : null)} className="h-8 w-36 bg-zinc-800 border-zinc-700 text-sm" />
            </div>
            {(item.start_date || item.end_date) && (
              <button onClick={() => { onUpdateSchedule(item.event_id, 'start_date', null); onUpdateSchedule(item.event_id, 'end_date', null); }} className="text-xs text-zinc-500 hover:text-zinc-300">Limpiar fechas</button>
            )}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => onRemove(item.event_id)} className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"><Trash2 size={16} /></Button>
      </div>
    </div>
  );
};

const FeaturedEventsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({ events: [], max_events: MAX_FEATURED_EVENTS, is_enabled: true });
  const [allEvents, setAllEvents] = useState([]);
  const [eventsMap, setEventsMap] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [configRes, eventsRes] = await Promise.all([
        api.get('/settings/featured-events').catch(() => ({ data: { events: [], max_events: MAX_FEATURED_EVENTS, is_enabled: true } })),
        api.get('/events/all').catch(() => ({ data: [] }))
      ]);
      setConfig(configRes.data || { events: [], max_events: MAX_FEATURED_EVENTS, is_enabled: true });
      const activeEvents = (eventsRes.data || []).filter(e => !['draft', 'cancelled', 'completed'].includes(e.status));
      setAllEvents(activeEvents);
      const map = {};
      activeEvents.forEach(e => { map[e.id] = e; });
      setEventsMap(map);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/featured-events', config);
      toast.success('Eventos destacados guardados');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setConfig(prev => {
        const oldIndex = prev.events.findIndex(e => e.event_id === active.id);
        const newIndex = prev.events.findIndex(e => e.event_id === over.id);
        const newEvents = arrayMove(prev.events, oldIndex, newIndex).map((e, i) => ({ ...e, order: i }));
        return { ...prev, events: newEvents };
      });
    }
  };

  const handleAddEvent = (eventId) => {
    if (config.events.length >= MAX_FEATURED_EVENTS) { toast.error('Maximo ' + MAX_FEATURED_EVENTS + ' eventos destacados'); return; }
    if (config.events.some(e => e.event_id === eventId)) { toast.error('Este evento ya esta destacado'); return; }
    setConfig(prev => ({ ...prev, events: [...prev.events, { event_id: eventId, order: prev.events.length, start_date: null, end_date: null }] }));
    setShowAddModal(false);
    toast.success('Evento agregado');
  };

  const handleRemoveEvent = (eventId) => {
    setConfig(prev => ({ ...prev, events: prev.events.filter(e => e.event_id !== eventId).map((e, i) => ({ ...e, order: i })) }));
  };

  const handleUpdateSchedule = (eventId, field, value) => {
    setConfig(prev => ({ ...prev, events: prev.events.map(e => e.event_id === eventId ? { ...e, [field]: value } : e) }));
  };

  const availableEvents = allEvents.filter(e => !config.events.some(fe => fe.event_id === e.id) && (e.title.toLowerCase().includes(searchTerm.toLowerCase()) || e.location?.toLowerCase().includes(searchTerm.toLowerCase())));

  if (loading) {
    return (<SidebarProvider><AdminLayout><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#D4AF37]" size={32} /></div></AdminLayout></SidebarProvider>);
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="featured-events-page">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-black uppercase tracking-tight flex items-center gap-3"><Star className="text-[#D4AF37]" />Banner de Eventos Destacados</h1>
              <p className="text-zinc-400 mt-1">Configura los eventos que aparecen en el banner principal (max. {MAX_FEATURED_EVENTS})</p>
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold">
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}Guardar Cambios
            </Button>
          </div>

          {/* Campaign shortcut — featured events deserve a marketing push */}
          {config.is_enabled && config.events.length > 0 && (
            <div className="mb-6 bg-gradient-to-r from-[#D4AF37]/10 via-amber-500/5 to-transparent border border-[#D4AF37]/30 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="featured-campaign-cta">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/15 flex items-center justify-center">
                  <Megaphone className="text-[#D4AF37]" size={18} />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">¿Querés avisar sobre estos destacados?</p>
                  <p className="text-zinc-400 text-xs">Lanzá una campaña de email a los usuarios de la plataforma para amplificar la difusión.</p>
                </div>
              </div>
              <div className="flex gap-2">
                {config.events.slice(0, 1).map(item => (
                  <Link key={item.event_id} to={`/admin/campanas/nueva?event=${item.event_id}`}>
                    <Button size="sm" className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold" data-testid="featured-launch-campaign-btn">
                      <Megaphone size={14} className="mr-2" /> Crear campaña con este evento
                    </Button>
                  </Link>
                ))}
                <Link to="/admin/campanas/nueva">
                  <Button size="sm" variant="outline" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10">
                    Campaña genérica
                  </Button>
                </Link>
              </div>
            </div>
          )}

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={config.is_enabled} onChange={(e) => setConfig(prev => ({ ...prev, is_enabled: e.target.checked }))} className="w-5 h-5 rounded border-zinc-700 bg-zinc-800 text-[#D4AF37] focus:ring-[#D4AF37]" />
              <div>
                <span className="text-white font-medium">Banner de eventos destacados activo</span>
                <p className="text-xs text-zinc-500">Si esta desactivado, el banner mostrara los proximos eventos automaticamente</p>
              </div>
            </label>
          </div>

          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="text-[#D4AF37] flex-shrink-0 mt-0.5" size={18} />
            <div className="text-sm">
              <p className="text-[#D4AF37] font-medium">Como funciona</p>
              <p className="text-zinc-400 mt-1">Los eventos se mostraran en el orden que configures. Puedes programar fechas opcionales para que un evento destacado solo aparezca durante un periodo especifico.</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-white">Eventos Destacados ({config.events.length}/{MAX_FEATURED_EVENTS})</h2>
              <Button onClick={() => setShowAddModal(true)} disabled={config.events.length >= MAX_FEATURED_EVENTS} variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10">
                <Plus size={16} className="mr-2" />Agregar Evento
              </Button>
            </div>

            {config.events.length === 0 ? (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
                <Star size={48} className="mx-auto text-zinc-700 mb-4" />
                <p className="text-zinc-400">No hay eventos destacados configurados</p>
                <p className="text-zinc-500 text-sm mt-1">El banner mostrara los proximos eventos automaticamente</p>
                <Button onClick={() => setShowAddModal(true)} className="mt-4 bg-[#D4AF37] hover:bg-[#B8962E] text-black"><Plus size={16} className="mr-2" />Agregar Primer Evento</Button>
              </div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={config.events.map(e => e.event_id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-3">
                    {config.events.map((item) => (<SortableFeaturedEvent key={item.event_id} item={item} event={eventsMap[item.event_id]} onRemove={handleRemoveEvent} onUpdateSchedule={handleUpdateSchedule} />))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </motion.div>

        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><Plus className="text-[#D4AF37]" size={20} />Agregar Evento Destacado</DialogTitle></DialogHeader>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
              <Input placeholder="Buscar evento por nombre o ubicacion..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-zinc-800 border-zinc-700" />
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 pr-2">
              {availableEvents.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar size={40} className="mx-auto text-zinc-700 mb-3" />
                  <p className="text-zinc-400">No hay eventos disponibles</p>
                  <p className="text-zinc-500 text-sm">{allEvents.length === 0 ? 'No hay eventos publicados' : 'Todos los eventos ya estan destacados'}</p>
                </div>
              ) : (
                availableEvents.map((event) => {
                  const eventDate = new Date(event.date);
                  return (
                    <div key={event.id} className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 hover:border-[#D4AF37]/50 transition-colors cursor-pointer" onClick={() => handleAddEvent(event.id)} data-testid={`add-event-${event.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-12 rounded overflow-hidden bg-zinc-700 flex-shrink-0">
                          {event.image_url ? (<img src={event.image_url} alt="" className="w-full h-full object-cover" />) : (<div className="w-full h-full flex items-center justify-center"><Image size={16} className="text-zinc-500" /></div>)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-white truncate">{event.title}</h4>
                          <div className="flex items-center gap-3 text-xs text-zinc-400 mt-1">
                            <span>{formatInTimeZone(eventDate, 'America/Argentina/Buenos_Aires', "d MMM yyyy", { locale: es })}</span>
                            <span>{event.location}</span>
                          </div>
                        </div>
                        <Plus size={18} className="text-[#D4AF37] flex-shrink-0" />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </DialogContent>
        </Dialog>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default FeaturedEventsPage;
